import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart3 } from "lucide-react";

interface SalaryPageInsightProps {
  occupationTitle: string;
  locationName: string;
  meanSalary: number;
  medianSalary: number;
  hourlyMean?: number;
  employment?: number;
  p10?: number;
  p90?: number;
  /** OEWS / data vintage year when available */
  dataYear?: number | string | null;
}

const currency = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.round(n));

const count = (n: number) => new Intl.NumberFormat('en-US').format(Math.round(n));

/**
 * Above-the-fold, data-derived copy unique to each occupation × location page.
 * Built so Google (and readers) see page-specific numbers and analysis rather
 * than interchangeable boilerplate — the core fix for "Crawled - currently
 * not indexed" on the programmatic salary catalogue.
 */
export default function SalaryPageInsight({
  occupationTitle,
  locationName,
  meanSalary,
  medianSalary,
  hourlyMean,
  employment,
  p10,
  p90,
  dataYear,
}: SalaryPageInsightProps) {
  if (!meanSalary && !medianSalary) return null;

  const mean = meanSalary || medianSalary;
  const median = medianSalary || meanSalary;
  const spread =
    p10 && p90 && p10 > 0
      ? Math.round(((p90 - p10) / p10) * 100)
      : null;
  const meanAboveMedian = mean > 0 && median > 0 ? mean - median : 0;
  const vintage = dataYear || new Date().getFullYear();
  const updatedLabel = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  });

  const skewSentence =
    meanAboveMedian > 2000
      ? ` Mean pay (${currency(mean)}) sits ${currency(meanAboveMedian)} above the median, which usually means a longer right tail — a smaller share of higher-paid roles pulls the average up.`
      : meanAboveMedian < -2000
        ? ` Mean pay sits below the median here, which is uncommon and often reflects a concentration of wages around the middle of the distribution.`
        : ` Mean and median are close, so pay is relatively concentrated around ${currency(median)}.`;

  const rangeSentence =
    p10 && p90
      ? ` Entry-level roles (10th percentile) report about ${currency(p10)}; experienced workers at the 90th percentile report about ${currency(p90)}${
          spread != null ? ` — a ${spread}% spread from bottom to top` : ''
        }.`
      : '';

  const employmentSentence =
    employment && employment > 0
      ? ` BLS counts roughly ${count(employment)} ${occupationTitle.toLowerCase()} jobs in ${locationName}.`
      : '';

  const hourlySentence =
    hourlyMean && hourlyMean > 0
      ? ` That works out to about $${hourlyMean.toFixed(2)} per hour on average.`
      : '';

  return (
    <Card className="mb-6 border-wage-primary/20 bg-wage-primary/[0.03]">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="h-5 w-5 text-wage-primary" />
            How much do {occupationTitle} make in {locationName}?
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="font-normal">
              OEWS {vintage}
            </Badge>
            <Badge variant="outline" className="font-normal">
              Updated {updatedLabel}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-foreground leading-relaxed">
          The average {occupationTitle.toLowerCase()} salary in {locationName} is{' '}
          <strong>{currency(mean)}</strong> per year
          {median !== mean ? (
            <>
              , with a median of <strong>{currency(median)}</strong>
            </>
          ) : null}
          .{hourlySentence}
          {employmentSentence}
        </p>
        <p className="text-muted-foreground leading-relaxed text-sm">
          {skewSentence.trim()}
          {rangeSentence}
          {' '}
          Figures are employer-reported wages from the Bureau of Labor Statistics
          Occupational Employment and Wage Statistics program — not self-reported
          survey averages — so they are the right baseline for offers, raises, and
          relocation decisions in this market.
        </p>
      </CardContent>
    </Card>
  );
}
